import * as React from 'react';
import { Route, type RouteProps } from 'react-router';

import { Route as CourseraRoute } from '@coursera/react-router';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import gateOnUnifiedEnterprise from 'bundles/enterprise-learner-search/lib/gateOnUnifiedEnterprise';
import { ProgramHomePageTypes } from 'bundles/enterprise-legacy-learner-home/constants/ProgramHomeConstants';
import RouteNames from 'bundles/enterprise-legacy-learner-home/constants/RouteNames';
import UnifiedUserExperienceHandler from 'bundles/unified-common/providers/UnifiedUserExperienceHandler';
import EnterprisePageWrapper from 'bundles/unified-home-common/pages/enterprise/EnterprisePageWrapper';

const EnterpriseSearchPageRoot = loadOnRoute(
  () => import('bundles/enterprise-learner-search/pages/EnterpriseSearchPageRoot')
);
const EnterpriseBrowseDomainPageRoot = loadOnRoute(
  () => import('bundles/enterprise-learner-search/pages/EnterpriseBrowseDomainPageRoot')
);

const NewEnterpriseSearchPageRoot = gateOnUnifiedEnterprise(
  loadOnRoute(() => import('bundles/search-common/pages/enterprise/EnterpriseSearchPageRoot'))
);

const NotFound = loadOnRoute(() => import('bundles/phoenix/components/NotFound'));

type GetComponentFunc = NonNullable<RouteProps['getComponent']>;

// If a user pastes the url wrong (e.g. twice), we'll have an array value for query. Normally this is OK
// (the page will crash), but the terrible Algolia library does some weirdo SSR render that triggers
// infra alerts. If we detect this case, return a NotFound, squelching the errors.
const GuardedEnterpriseSearchPage: GetComponentFunc = (nextState, callback) => {
  const query = nextState.location.query?.query;
  const getComponent: GetComponentFunc = Array.isArray(query) ? NotFound : EnterpriseSearchPageRoot;

  return getComponent(nextState, callback);
};

const EnterprisePageWrapperWithUserExperienceHandler = (props: React.ComponentProps<typeof EnterprisePageWrapper>) => (
  <UnifiedUserExperienceHandler>
    <EnterprisePageWrapper {...props} />
  </UnifiedUserExperienceHandler>
);

const routes = (
  <CourseraRoute
    handle={{
      tracking: {
        data: (params) => ({
          page: { segment: 'enterprise' },
          enterprise: {
            ...(params?.programSlug ? { programSlug: params.programSlug } : {}),
          },
        }),
      },
    }}
  >
    <CourseraRoute
      name={RouteNames.PROGRAM_HOME}
      path="programs(/:programSlug)"
      component={EnterprisePageWrapperWithUserExperienceHandler}
      handle={{
        tracking: {
          data: (_, location) => {
            const currentTab = location?.query?.currentTab as keyof typeof ProgramHomePageTypes;

            return {
              page: { type: ProgramHomePageTypes[currentTab] ?? ProgramHomePageTypes.CATALOG },
            };
          },
        },
      }}
    >
      <CourseraRoute
        name={RouteNames.BROWSE}
        path="browse"
        getComponent={GuardedEnterpriseSearchPage}
        handle={{
          tracking: {
            data: (_, location) => {
              const currentPage = location?.query?.page;

              return {
                page: {
                  type: ProgramHomePageTypes.ENTERPRISE_SEARCH,
                  pagination: {
                    pageNumber: currentPage ? Number(currentPage) : 1,
                  },
                },
              };
            },
          },
        }}
      />
      <CourseraRoute
        name={RouteNames.DOMAIN_SUBDOMAIN_COLLECTIONS}
        path=":domainSlug(/:subdomainSlug)"
        getComponent={EnterpriseBrowseDomainPageRoot}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.PROGRAM_SKILLS },
            }),
          },
        }}
      />
    </CourseraRoute>
    <Route path="/organizations/:orgSlug/search" getComponent={NewEnterpriseSearchPageRoot} />
    <Route path="*" getComponent={NotFound} />
  </CourseraRoute>
);

export default routes;
